import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw, CheckCircle, AlertCircle } from 'lucide-react';
import M12Data from '../../data/mathExercises/M12_ArithmeticGeometricSequences.js';
import PDFControls from '../PDFControls';

const M12ExerciseSet = () => {
  // ========== 數據獲取 ==========
  const { moduleInfo, topics, problemsByTopic } = M12Data;
  
  // ========== 狀態管理 ==========
  const [selectedTopic, setSelectedTopic] = useState(topics[0]); // 默認選擇第一個主題
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [showSolution, setShowSolution] = useState({ a: false, b: false, c: false });
  const [showHint, setShowHint] = useState({ a: false, b: false, c: false });
  const [completedProblems, setCompletedProblems] = useState(new Set());
  
  const currentTopicProblems = selectedTopic ? problemsByTopic[selectedTopic.id] || [] : [];
  const currentProblem = currentTopicProblems[currentProblemIndex];

  // ========== 重置狀態 ==========
  const resetProblemState = () => {
    setShowSolution({ a: false, b: false, c: false });
    setShowHint({ a: false, b: false, c: false });
  };

  // ========== 主題選擇 ==========
  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    setCurrentProblemIndex(0);
    resetProblemState();
  };

  // ========== 導航控制 ==========
  const handlePrevious = () => {
    if (currentProblemIndex > 0) {
      setCurrentProblemIndex(prev => prev - 1);
      resetProblemState();
    }
  };

  const handleNext = () => {
    if (currentProblemIndex < currentTopicProblems.length - 1) {
      setCurrentProblemIndex(prev => prev + 1);
      resetProblemState();
    }
  };



  // ========== 解答顯示控制 ==========
  const toggleSolution = (part) => {
    setShowSolution(prev => ({
      ...prev,
      [part]: !prev[part]
    }));
  };

  const toggleHint = (part) => {
    setShowHint(prev => ({
      ...prev,
      [part]: !prev[part]
    }));
  };





  // ========== 主渲染 ==========
  return (
    <div className="min-h-screen bg-gray-100">
      {/* 頭部標題 */}
      <div className="bg-white shadow-sm border-b p-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-800">數學學習系統 - 等差數列與等比數列</h1>
          <div className="flex items-center mt-2">
            <div className="flex items-center mr-8">
              <span className="text-yellow-500 mr-2">🏆</span>
              <span className="text-gray-600">0%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 左側邊欄 - 學習主題 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4">學習主題</h3>
              <div className="space-y-2">
                {topics.map((topic, index) => {
                  const topicProblems = problemsByTopic[topic.id] || [];
                  const completedCount = topicProblems.filter(p => completedProblems.has(p.id)).length;
                  const progressPercent = topicProblems.length > 0 ? (completedCount / topicProblems.length) * 100 : 0;
                  
                  return (
                    <button
                      key={topic.id}
                      onClick={() => handleTopicSelect(topic)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedTopic?.id === topic.id
                          ? 'bg-blue-100 border border-blue-300 text-blue-800'
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-sm">{index + 1}. {topic.title}</span>
                        <span className="text-xs text-gray-500">{Math.round(progressPercent)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1">
                        <div 
                          className="bg-blue-500 h-1 rounded-full transition-all duration-300"
                          style={{ width: `${progressPercent}%` }}
                        ></div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 右側主要內容區 */}
          <div className="lg:col-span-3">
            {currentProblem ? (
              <div className="bg-white rounded-lg shadow-lg p-6">
                {/* 題目標題區 */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">{selectedTopic?.title}</h2>
                    <p className="text-gray-600 text-sm mt-1">{selectedTopic?.description}</p>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <span>📖 {currentProblemIndex + 1} / {currentTopicProblems.length}</span>
                  </div>
                </div>

                {/* 題目導航 */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex space-x-2">
                    {currentTopicProblems.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setCurrentProblemIndex(index);
                          resetProblemState();
                        }}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                          currentProblemIndex === index
                            ? 'bg-blue-600 text-white'
                            : completedProblems.has(currentTopicProblems[index]?.id)
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {completedProblems.has(currentTopicProblems[index]?.id) ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          index + 1
                        )}
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handlePrevious}
                      disabled={currentProblemIndex === 0}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={currentProblemIndex === currentTopicProblems.length - 1}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* PDF 生成工具 */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="text-blue-600">●</span>
                    <span className="font-medium text-blue-800">PDF 生成工具</span>
                  </div>
                  <PDFControls
                    currentQuestion={currentProblem}
                    currentQuestionNumber={currentProblemIndex + 1}
                    allTopicQuestions={currentTopicProblems}
                    topicInfo={selectedTopic}
                    moduleInfo={moduleInfo}
                    className="pdf-controls-m12"
                  />
                </div>

                {/* 問題內容 */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">問題 {currentProblemIndex + 1}:</h3>
                    <span className={`px-2 py-1 rounded text-sm ${
                      currentProblem.difficulty === 1 ? 'bg-green-100 text-green-700' :
                      currentProblem.difficulty === 2 ? 'bg-yellow-100 text-yellow-700' :
                      currentProblem.difficulty === 3 ? 'bg-red-100 text-red-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {currentProblem.difficulty === 1 ? '基礎' :
                       currentProblem.difficulty === 2 ? '中級' :
                       currentProblem.difficulty === 3 ? '高級' : '專家'}
                    </span>
                  </div>
                  
                  {/* 主題目描述 */}
                  <div className="bg-blue-50 p-4 rounded-lg mb-6">
                    <p className="text-gray-800 text-lg font-medium">{currentProblem.question}</p>
                  </div>

                  {/* 三個部分的題目和解答 */}
                  {['a', 'b', 'c'].map((part) => (
                    <div key={part} className="mb-6 border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-medium text-gray-800">
                          ({part}) {currentProblem.parts[part]}
                        </h4>
                      </div>

                      {/* 提示和解答按鈕 */}
                      <div className="flex items-center space-x-2 mb-3">
                        <button
                          onClick={() => toggleHint(part)}
                          className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
                        >
                          {showHint[part] ? '隱藏提示' : '顯示提示'}
                        </button>
                        <button
                          onClick={() => toggleSolution(part)}
                          className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          {showSolution[part] ? '隱藏解答' : '顯示解答'}
                        </button>
                      </div>

                      {/* 提示內容 */}
                      {showHint[part] && (
                        <div className="mb-3 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
                          <p className="text-sm text-yellow-800">
                            <strong>提示：</strong>{currentProblem.hint[part]}
                          </p>
                        </div>
                      )}

                      {/* 解答內容 */}
                      {showSolution[part] && (
                        <div className="p-4 bg-white border border-blue-200 rounded-lg">
                          <h5 className="font-medium text-blue-800 mb-2">詳細解答：</h5>
                          <div className="text-sm text-gray-700 whitespace-pre-line">
                            {currentProblem.solution[part]}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* 標記完成按鈕 */}
                  <div className="flex justify-center space-x-3 mt-6">
                    <button
                      onClick={() => {
                        const newCompleted = new Set(completedProblems);
                        if (newCompleted.has(currentProblem.id)) {
                          newCompleted.delete(currentProblem.id);
                        } else {
                          newCompleted.add(currentProblem.id);
                        }
                        setCompletedProblems(newCompleted);
                      }}
                      className={`flex items-center space-x-2 px-6 py-2 rounded-lg transition-colors ${
                        completedProblems.has(currentProblem.id)
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>{completedProblems.has(currentProblem.id) ? '已完成' : '標記完成'}</span>
                    </button>
                    <button
                      onClick={resetProblemState}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span>重置</span>
                    </button>
                  </div>

                  {/* 標籤 */}
                  {currentProblem.tags && currentProblem.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {currentProblem.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">請選擇一個學習主題</h3>
                <p className="text-gray-500">從左側選擇一個主題開始練習</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default M12ExerciseSet; 